// ** Redux Imports
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'

// ** API Imports
import * as intermediariesApi from '../api'

/**
 * Llave con la que este reducer está montado en el rootReducer.
 *
 * No coincide con el `name` del slice ('appIntermediaries'): ese sólo prefija los
 * action types. Todo acceso al store debe pasar por aquí.
 */
export const INTERMEDIARIES_STATE_KEY = 'intermediaries'

const selectIntermediariesSlice = (state) => state?.[INTERMEDIARIES_STATE_KEY]

/**
 * Tiempo de vida de cada lista en el store.
 *
 * Sustituyen a los `setInterval(enableRequest)` que vivían en las vistas —60s en
 * TableMovements, 300s en Cash— y que compartían una sola bandera global: el primer
 * dataset en responder apagaba el caché de los otros tres. El resumen conserva su
 * ventana más larga porque es un agregado más caro de calcular en el backend.
 */
export const INTERMEDIARIES_CACHE_TTL = 60 * 1000
export const INTERMEDIARIES_RESUME_CACHE_TTL = 5 * 60 * 1000

// Lecturas: cada una se pide, se fecha y se invalida por separado
const LIST = 'data'
const RESUME = 'intermediaryResume'
const SEARCH = 'searchIntermediary'

// Escrituras: su estado va aparte para que guardar no ponga en "cargando" a las listas
const ADD = 'add'
const UPDATE = 'update'
const DEACTIVATE = 'deactivate'

// status: idle | pending | fulfilled | rejected
const emptyReadState = () => ({ status: 'idle', error: null, lastFetch: null, forceFetch: false })
const emptyWriteState = () => ({ status: 'idle', error: null })

const initialReads = () => ({
    [LIST]: emptyReadState(),
    [RESUME]: emptyReadState(),
    // Sin TTL: el detalle de un intermediario siempre se pide al backend
    [SEARCH]: emptyReadState()
})

const initialWrites = () => ({
    [ADD]: emptyWriteState(),
    [UPDATE]: emptyWriteState(),
    [DEACTIVATE]: emptyWriteState()
})

const readState = (state, key) => {
    if (!state.reads[key]) {
        state.reads[key] = emptyReadState()
    }
    return state.reads[key]
}

const writeState = (state, key) => {
    if (!state.writes[key]) {
        state.writes[key] = emptyWriteState()
    }
    return state.writes[key]
}

//Sin respuesta HTTP (timeout, CORS, backend caído) el payload viene vacío: se cae al
//mensaje del propio error para no guardar `undefined`
const readError = (action) => action.payload ?? action.error?.message ?? null

const startRead = (state, key) => {
    const entry = readState(state, key)
    entry.status = 'pending'
    entry.error = null
    //Se consume aquí para que una invalidación durante la petición siga vigente:
    //la respuesta en vuelo no la describe
    entry.forceFetch = false
}

const finishRead = (state, key) => {
    const entry = readState(state, key)
    entry.status = 'fulfilled'
    entry.error = null
    entry.lastFetch = Date.now()
}

const failRead = (state, key, action) => {
    const entry = readState(state, key)
    entry.status = 'rejected'
    entry.error = readError(action)
    //Permite reintentar de inmediato: si el fallo ocurrió en un refetch con datos
    //previos, el TTL bloquearía la siguiente petición durante todo el periodo de caché
    entry.forceFetch = true
}

//El endpoint debe devolver una lista: cualquier otra forma dejaría la pantalla con un
//`data.map` roto
const asList = (data) => (Array.isArray(data) ? data : [])

/**
 * ¿Hace falta pedir esta lista al backend?
 *
 * Va en la opción `condition` del thunk y no dentro del payload creator: así la
 * petición ni siquiera arranca cuando el caché sirve, y dos componentes montando a
 * la vez no disparan dos GET idénticos.
 */
const shouldFetch = (state, { key, ttl, force }) => {
    const slice = selectIntermediariesSlice(state)

    if (!slice) return true

    const entry = slice.reads?.[key]

    if (!entry) return true

    // Ya hay una lectura en vuelo (las escrituras tienen su propio estado)
    if (entry.status === 'pending') return false

    // Invalidación explícita (`{ force: true }` o invalidateIntermediaries) o lista vacía
    if (force || entry.forceFetch || !slice[key]?.length) return true

    return Date.now() - (entry.lastFetch ?? 0) > ttl
}

/**
 * `searchIntermediary` es el intermediario abierto en pantalla: un objeto, no una lista.
 *
 * El id se compara como texto porque unas veces viene del backend (número) y otras
 * de un <Select> de la vista, donde puede llegar como string.
 */
const isSameIntermediary = (current, id) => current !== null && current !== undefined && id !== undefined && String(current.id) === String(id)

/**
 * Lista de intermediarios.
 *
 * Para saltarse el caché: `dispatch(getIntermediaries({ force: true }))`.
 */
export const getIntermediaries = createAsyncThunk(
    'appIntermediaries/getIntermediaries',
    async (_, { rejectWithValue }) => {
        try {
            return {
                data: await intermediariesApi.fetchIntermediaries()
            }
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    },
    {
        condition: (arg, { getState }) => {
            return shouldFetch(getState(), {
                key: LIST,
                ttl: INTERMEDIARIES_CACHE_TTL,
                force: arg?.force
            })
        }
    }
)

export const getIntermediariesResume = createAsyncThunk(
    'appIntermediaries/getIntermediariesResume',
    async (_, { rejectWithValue }) => {
        try {
            return {
                data: await intermediariesApi.fetchIntermediariesResume()
            }
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    },
    {
        condition: (arg, { getState }) => shouldFetch(getState(), {
            key: RESUME,
            ttl: INTERMEDIARIES_RESUME_CACHE_TTL,
            force: arg?.force
        })
    }
)

export const getIntermediary = createAsyncThunk('appIntermediaries/getIntermediary', async (id, { rejectWithValue }) => {
    try {
        return {
            data: await intermediariesApi.fetchIntermediary(id)
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const addIntermediary = createAsyncThunk('appIntermediaries/addIntermediary', async (intermediary, { dispatch, rejectWithValue }) => {
    try {
        const response = await intermediariesApi.createIntermediary(intermediary)
        await dispatch(getIntermediaries({ force: true }))
        return response
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const updateIntermediary = createAsyncThunk('appIntermediaries/updateIntermediary', async (intermediary, { dispatch, rejectWithValue }) => {
    try {
        const response = await intermediariesApi.updateIntermediary(intermediary)
        await dispatch(getIntermediaries({ force: true }))
        return response
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const deactivateIntermediary = createAsyncThunk('appIntermediaries/deactivateIntermediary', async (id, { dispatch, rejectWithValue }) => {
    try {
        const response = await intermediariesApi.deactivateIntermediary(id)
        await dispatch(getIntermediaries({ force: true }))
        return response
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const appIntermediariesSlice = createSlice({
    name: 'appIntermediaries',
    initialState: {
        data: [],
        // Objeto (o null si no hay ninguno abierto): nunca fue una lista
        searchIntermediary: null,
        intermediaryResume: [],
        // Estado de cada lectura: status, error y metadatos de caché
        reads: initialReads(),
        // Estado de cada escritura, separado del de las lecturas
        writes: initialWrites()
    },
    reducers: {
        invalidateIntermediaries: (state) => {
            Object.values(state.reads).forEach((entry) => {
                entry.forceFetch = true
            })
        },
        resetIntermediariesError: (state) => {
            Object.values(state.reads).forEach((entry) => {
                entry.error = null
            })
            Object.values(state.writes).forEach((entry) => {
                entry.error = null
            })
        }
    },
    extraReducers: builder => {
        builder.addCase(getIntermediaries.pending, (state) => {
            startRead(state, LIST)
        })
        builder.addCase(getIntermediaries.fulfilled, (state, action) => {
            state.data = asList(action.payload.data)
            finishRead(state, LIST)
        })
        builder.addCase(getIntermediaries.rejected, (state, action) => {
            failRead(state, LIST, action)
        })

        builder.addCase(getIntermediariesResume.pending, (state) => {
            startRead(state, RESUME)
        })
        builder.addCase(getIntermediariesResume.fulfilled, (state, action) => {
            state.intermediaryResume = asList(action.payload.data)
            finishRead(state, RESUME)
        })
        builder.addCase(getIntermediariesResume.rejected, (state, action) => {
            failRead(state, RESUME, action)
        })

        builder.addCase(getIntermediary.pending, (state, action) => {
            if (!isSameIntermediary(state.searchIntermediary, action.meta.arg)) {
                state.searchIntermediary = null
            }
            startRead(state, SEARCH)
        })
        builder.addCase(getIntermediary.fulfilled, (state, action) => {
            state.searchIntermediary = action.payload.data
            finishRead(state, SEARCH)
        })
        builder.addCase(getIntermediary.rejected, (state, action) => {
            // Sin esto, un GET fallido deja en pantalla el saldo del intermediario anterior
            state.searchIntermediary = null
            failRead(state, SEARCH, action)
        })

        // ESCRITURAS — los datos los actualiza la recarga que dispara cada thunk, así el
        // caché nunca se renueva con algo que el backend no confirmó
        builder.addCase(addIntermediary.pending, (state) => {
            const entry = writeState(state, ADD)
            entry.status = 'pending'
            entry.error = null
        })
        builder.addCase(addIntermediary.fulfilled, (state) => {
            writeState(state, ADD).status = 'fulfilled'
            //Los agregados del resumen cambian con cada escritura, pero sólo los mira
            //Cash: se marcan para relectura en vez de pedirlos aquí
            readState(state, RESUME).forceFetch = true
        })
        builder.addCase(addIntermediary.rejected, (state, action) => {
            const entry = writeState(state, ADD)
            entry.status = 'rejected'
            entry.error = readError(action)
        })

        builder.addCase(updateIntermediary.pending, (state) => {
            const entry = writeState(state, UPDATE)
            entry.status = 'pending'
            entry.error = null
        })
        builder.addCase(updateIntermediary.fulfilled, (state) => {
            writeState(state, UPDATE).status = 'fulfilled'
            //Los agregados del resumen cambian con cada escritura, pero sólo los mira
            //Cash: se marcan para relectura en vez de pedirlos aquí
            readState(state, RESUME).forceFetch = true
        })
        builder.addCase(updateIntermediary.rejected, (state, action) => {
            const entry = writeState(state, UPDATE)
            entry.status = 'rejected'
            entry.error = readError(action)
        })

        builder.addCase(deactivateIntermediary.pending, (state) => {
            const entry = writeState(state, DEACTIVATE)
            entry.status = 'pending'
            entry.error = null
        })
        builder.addCase(deactivateIntermediary.fulfilled, (state) => {
            writeState(state, DEACTIVATE).status = 'fulfilled'
            //Los agregados del resumen cambian con cada escritura, pero sólo los mira
            //Cash: se marcan para relectura en vez de pedirlos aquí
            readState(state, RESUME).forceFetch = true
        })
        builder.addCase(deactivateIntermediary.rejected, (state, action) => {
            const entry = writeState(state, DEACTIVATE)
            entry.status = 'rejected'
            entry.error = readError(action)
        })
    }
})

export const { invalidateIntermediaries, resetIntermediariesError } = appIntermediariesSlice.actions

// Selectores — datos
export const selectIntermediaries = (state) => selectIntermediariesSlice(state)?.data ?? []
/**
 * Los intermediarios vigentes.
 *
 * `GET /intermediaries/` devuelve todos, activos y dados de baja, así que esto es un
 * filtro derivado y no una segunda lista en el store: hasta ahora `activeIntermediaries`
 * era una petición aparte (`?include_inactive=`) que traía lo mismo.
 * Va memoizado porque `filter` crea un array nuevo en cada llamada y `useSelector`
 * compara por referencia: sin memo, cualquier acción re-renderizaría a quien lo use.
 */
export const selectActiveIntermediaries = createSelector(
    [selectIntermediaries],
    (intermediaries) => intermediaries.filter((intermediary) => intermediary.is_active)
)
export const selectIntermediaryResume = (state) => selectIntermediariesSlice(state)?.intermediaryResume ?? []
export const selectSearchIntermediary = (state) => selectIntermediariesSlice(state)?.searchIntermediary ?? null

// Selectores — estado de las lecturas
const selectRead = (state, key) => selectIntermediariesSlice(state)?.reads?.[key]

export const selectIntermediariesStatus = (state) => selectRead(state, LIST)?.status ?? 'idle'
export const selectIntermediariesLoading = (state) => selectRead(state, LIST)?.status === 'pending'
export const selectIntermediariesError = (state) => selectRead(state, LIST)?.error ?? null

export const selectIntermediaryResumeLoading = (state) => selectRead(state, RESUME)?.status === 'pending'
export const selectIntermediaryResumeError = (state) => selectRead(state, RESUME)?.error ?? null

export const selectSearchIntermediaryLoading = (state) => selectRead(state, SEARCH)?.status === 'pending'
export const selectSearchIntermediaryError = (state) => selectRead(state, SEARCH)?.error ?? null

// Selectores — estado de las escrituras
const selectWrite = (state, key) => selectIntermediariesSlice(state)?.writes?.[key]

export const selectAddIntermediaryLoading = (state) => selectWrite(state, ADD)?.status === 'pending'
export const selectAddIntermediaryError = (state) => selectWrite(state, ADD)?.error ?? null
export const selectUpdateIntermediaryLoading = (state) => selectWrite(state, UPDATE)?.status === 'pending'
export const selectUpdateIntermediaryError = (state) => selectWrite(state, UPDATE)?.error ?? null
export const selectDeactivateIntermediaryLoading = (state) => selectWrite(state, DEACTIVATE)?.status === 'pending'
export const selectDeactivateIntermediaryError = (state) => selectWrite(state, DEACTIVATE)?.error ?? null

export default appIntermediariesSlice.reducer
